import { useState, useEffect, useRef } from "react";
import { API } from "@/lib/constants";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Presentation, 
  Download, 
  ExternalLink, 
  Calendar, 
  Building, 
  FileText,
  Eye,
  Palette,
  Search,
  Filter,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertCircle,
  Upload,
  Loader2,
  Trash2,
  FileUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Presentations() {
  const navigate = useNavigate();
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [brandedUploads, setBrandedUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadClientName, setUploadClientName] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPresentations();
    fetchBrandedUploads();
  }, []);

  const fetchBrandedUploads = async () => {
    try {
      const response = await fetch(`${API}/presentations/uploads`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBrandedUploads(data.uploads || []);
      }
    } catch (error) {
      console.error("Error fetching branded uploads:", error);
    }
  };

  const fetchPresentations = async () => {
    try {
      const response = await fetch(`${API}/proposals`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Filter only proposals that have Gamma generation
        const withPresentations = data.filter(
          (p) => p.gamma_generation_id || p.gamma_url || p.branded_pptx_url
        );
        setPresentations(withPresentations);
      }
    } catch (error) {
      console.error("Error fetching presentations:", error);
      toast.error("Failed to load presentations");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPresentations();
    setRefreshing(false);
    toast.success("Presentations refreshed");
  };

  const handleRefreshStatus = async (proposalId) => {
    try {
      const response = await fetch(`${API}/proposals/${proposalId}/gamma-status`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === "completed") {
          toast.success("Presentation is ready!");
          fetchPresentations();
        } else if (data.status === "pending") {
          toast.info("Still generating... please wait");
        } else {
          toast.error("Generation failed");
        }
      }
    } catch (error) {
      toast.error("Failed to check status");
    }
  };

  const handleUploadAndBrand = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pptx")) {
      toast.error("Only .pptx files are supported");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("client_name", uploadClientName || file.name.replace(".pptx", ""));

      const response = await fetch(`${API}/presentations/upload-and-brand`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Branded successfully: ${data.slide_count} slides`);
        setUploadClientName("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        fetchBrandedUploads();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteUpload = async (uploadId) => {
    try {
      const response = await fetch(`${API}/presentations/uploads/${uploadId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Deleted");
        fetchBrandedUploads();
      }
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const getStatusBadge = (presentation) => {
    if (presentation.branded_pptx_url) {
      return (
        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
          <CheckCircle className="w-3 h-3 mr-1" />
          Branded & Ready
        </Badge>
      );
    }
    if (presentation.gamma_url) {
      return (
        <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
          <Presentation className="w-3 h-3 mr-1" />
          Gamma Ready
        </Badge>
      );
    }
    if (presentation.gamma_generation_id && !presentation.gamma_url) {
      return (
        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
          <Clock className="w-3 h-3 mr-1" />
          Generating...
        </Badge>
      );
    }
    return (
      <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">
        <AlertCircle className="w-3 h-3 mr-1" />
        Pending
      </Badge>
    );
  };

  const filteredPresentations = presentations.filter((p) => {
    const matchesSearch = 
      p.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.industry?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (statusFilter === "all") return matchesSearch;
    if (statusFilter === "branded") return matchesSearch && p.branded_pptx_url;
    if (statusFilter === "gamma") return matchesSearch && p.gamma_url && !p.branded_pptx_url;
    if (statusFilter === "pending") return matchesSearch && p.gamma_generation_id && !p.gamma_url;
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="presentations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Presentation className="w-7 h-7 text-purple-600" />
            Presentations
          </h1>
          <p className="text-slate-500 mt-1">
            View and download all generated Gamma presentations
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { handleRefresh(); fetchBrandedUploads(); }}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => navigate("/proposals")}
          >
            <FileText className="w-4 h-4 mr-2" />
            Create Proposal
          </Button>
        </div>
      </div>

      {/* Upload & Brand Section */}
      <Card className="border-dashed border-2 border-slate-200 hover:border-indigo-300 transition-colors" data-testid="upload-brand-section">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="p-3 bg-indigo-50 rounded-xl">
                <Upload className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Upload & Brand</h3>
                <p className="text-xs text-slate-500">Upload any .pptx — Rex applies RESILIGENT branding</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 flex-1 w-full sm:w-auto">
              <Input
                placeholder="Client name (optional)"
                value={uploadClientName}
                onChange={(e) => setUploadClientName(e.target.value)}
                className="sm:w-48"
                data-testid="upload-client-name"
              />
              <input
                type="file"
                accept=".pptx"
                ref={fileInputRef}
                onChange={(e) => handleUploadAndBrand(e.target.files[0])}
                className="hidden"
                data-testid="upload-file-input"
              />
              <Button
                variant="outline"
                className="border-indigo-200 hover:bg-indigo-50 text-indigo-700"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-brand-btn"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Branding...</>
                ) : (
                  <><FileUp className="w-4 h-4 mr-2" />Choose .pptx</>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Branded Uploads */}
      {brandedUploads.length > 0 && (
        <div className="space-y-3" data-testid="branded-uploads-section">
          <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
            <Palette className="w-5 h-5 text-green-600" />
            Branded Uploads ({brandedUploads.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {brandedUploads.map((u) => (
              <Card key={u.upload_id} className="hover:shadow-md transition-shadow" data-testid={`branded-upload-${u.upload_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
                        <Palette className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{u.client_name}</CardTitle>
                        <CardDescription className="text-xs">{u.original_filename}</CardDescription>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Branded
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Calendar className="w-3 h-3" />
                    {new Date(u.created_at).toLocaleDateString("en-GB", {
                      day: "numeric", month: "short", year: "numeric"
                    })}
                    <span className="mx-1">&bull;</span>
                    {u.slide_count} slides
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => window.open(`${API}/presentations/uploads/${u.upload_id}/download`, "_blank")}
                      data-testid={`download-upload-${u.upload_id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteUpload(u.upload_id)}
                      data-testid={`delete-upload-${u.upload_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by client name or industry..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-presentations"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Presentations</SelectItem>
                <SelectItem value="branded">Branded & Ready</SelectItem>
                <SelectItem value="gamma">Gamma Ready</SelectItem>
                <SelectItem value="pending">Generating</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Presentation className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{presentations.length}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Palette className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {presentations.filter((p) => p.branded_pptx_url).length}
                </p>
                <p className="text-xs text-slate-500">Branded</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Eye className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-600">
                  {presentations.filter((p) => p.gamma_url).length}
                </p>
                <p className="text-xs text-slate-500">Viewable</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">
                  {presentations.filter((p) => p.gamma_generation_id && !p.gamma_url).length}
                </p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Presentations Grid */}
      {filteredPresentations.length === 0 ? (
        <Card className="p-12 text-center">
          <Presentation className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">No Presentations Found</h3>
          <p className="text-slate-500 mb-4">
            {searchTerm || statusFilter !== "all"
              ? "Try adjusting your search or filters"
              : "Generate your first presentation from a proposal"}
          </p>
          <Button onClick={() => navigate("/proposals")}>
            <FileText className="w-4 h-4 mr-2" />
            Go to Proposals
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPresentations.map((p) => (
            <Card 
              key={p.proposal_id} 
              className="hover:shadow-md transition-shadow"
              data-testid={`presentation-card-${p.proposal_id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
                      <Building className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{p.client_name}</CardTitle>
                      <CardDescription className="text-xs">
                        {p.industry}
                      </CardDescription>
                    </div>
                  </div>
                  {getStatusBadge(p)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Calendar className="w-3 h-3" />
                  {new Date(p.created_at).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                  <span className="mx-1">•</span>
                  <span className="capitalize">
                    {p.proposal_type?.replace(/_/g, " ")}
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  {p.gamma_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => window.open(p.gamma_url, "_blank")}
                      data-testid={`view-gamma-${p.proposal_id}`}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  )}
                  
                  {p.branded_pptx_url && (
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => window.open(`${API}/proposals/${p.proposal_id}/download-branded-pptx`, "_blank")}
                      data-testid={`download-branded-${p.proposal_id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  )}

                  {p.gamma_generation_id && !p.gamma_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleRefreshStatus(p.proposal_id)}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Check Status
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/proposals/${p.proposal_id}`)}
                    data-testid={`view-proposal-${p.proposal_id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Proposal
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
